import {
  Component,
  ChangeDetectionStrategy,
  OnChanges,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ChangeDetectorRef,
  SimpleChanges,
  AfterViewChecked,
} from "@angular/core";
import { environment } from "../../../environments/environment";
import { Feature } from "../../interfaces/feature";
import { Epic } from "../../interfaces/epic";
import { VotingComponent } from "../voting/voting.component";
import { Project } from "../../interfaces/project";
import { FeatureService } from "../../services/feature/feature.service";
import { MzToastService, MzModalService } from "ng2-materialize";
import { ActivatedRoute } from "@angular/router";
import { ProjectService } from "../../services/project/project.service";
import { ConfirmStopVotingComponent } from "../confirm-stop-voting/confirm-stop-voting.component";
import { Observable } from "rxjs/Observable";
import { THIS_EXPR } from "@angular/compiler/src/output/output_ast";
import { EpicGalleryDialogComponent } from "../epic-gallery/epic-gallery.component";

@Component({
  selector: "app-item-carousel",
  templateUrl: "./item-carousel.component.html",
  styleUrls: ["./item-carousel.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

/**
 * item carousel component to list all items as clickable slides
 */
export class ItemCarouselComponent
  implements OnChanges, OnInit, AfterViewChecked {
  readonly env: any = environment;

  @Input() items: Array<Feature>;
  @Input() themes: Array<Epic>;
  @Input() itemIndex: number;
  @Input() allowDelete: boolean;

  @Output() carouselClosed: EventEmitter<any> = new EventEmitter<any>();
  @Output() deleteItemClicked: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild("voting") voting: VotingComponent;
  @ViewChild("Gallery") gallery: EpicGalleryDialogComponent;

  noMoreItemsToEstimate: boolean;

  project: Project;
  loading: boolean = true;
  projectId: any;
  estimateLink: string;

  constructor(
    private modalService: MzModalService,
    private featureService: FeatureService,
    private toastService: MzToastService,
    private changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    private projectService: ProjectService
  ) {}

  ngOnInit() {
    this.route.params.subscribe((params: { id: string }) => {
      this.loading = true;
      this.projectService
        .getProjectByShortId(params.id)
        .then((project: Project) => {
          this.project = project;
          this.projectId = this.project._id;
          this.estimateLink =
            this.env.agirUrl + "/planning-poker/" + this.project.shortId;
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
        });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["itemIndex"]) {
      if (this.itemIndex > -1 && this.items) {
        this.noMoreItemsToEstimate =
          this.items.filter(
            (x, i) => i > this.itemIndex && x.featureType !== "Bug"
          ).length === 0;
      }
    }
  }

  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  saveItem(item) {
    this.featureService.saveFeature(item);
  }

  deleteItem() {
    this.deleteItemClicked.emit(this.items[this.itemIndex]);
  }

  closeCarousel() {
    this.carouselClosed.emit();
    this.itemIndex = -1;
  }

  /**
   * closing carousel if clicking outside modal window
   * @param event
   */
  async close(event) {
    if (event.target.id === "itemCarouselModal") {
      if (this.voting && this.voting.isCurrentlyVoting()) {
        var confirm = await (<ConfirmStopVotingComponent>(
          this.modalService.open(ConfirmStopVotingComponent).instance
        )).result
          .asObservable()
          .first()
          .toPromise();
        if (!confirm) return;
        await this.voting.stopVoting();
      }
      this.closeCarousel();
    }
  }

  async startEstimationForNextItem() {
    var slideIndex = this.items
      .map((x, i) => {
        return { item: x, index: i };
      })
      .filter(
        (x) => x.index > this.itemIndex && x.item.featureType !== "Bug"
      )[0].index;
    this.setSlide(slideIndex);
    this.changeDetectorRef.detectChanges();
    await this.voting.startVoting();
    this.changeDetectorRef.detectChanges();
  }

  setSlide(index) {
    this.itemIndex = index;
    this.noMoreItemsToEstimate =
      this.items.filter((x, i) => i > this.itemIndex && x.featureType !== "Bug")
        .length === 0;
    this.changeDetectorRef.detectChanges();
  }

  async updateSlidesNext() {
    if (this.voting && this.voting.isCurrentlyVoting()) {
      var confirm = await (<ConfirmStopVotingComponent>(
        this.modalService.open(ConfirmStopVotingComponent).instance
      )).result
        .asObservable()
        .first()
        .toPromise();
      if (!confirm) return;
      await this.voting.stopVoting();
    }
    if (this.itemIndex < this.items.length - 1) {
      this.itemIndex++;
      this.noMoreItemsToEstimate =
        this.items.filter(
          (x, i) => i > this.itemIndex && x.featureType !== "Bug"
        ).length === 0;
    }
  }

  async updateSlidesPrev() {
    if (this.voting && this.voting.isCurrentlyVoting()) {
      var confirm = await (<ConfirmStopVotingComponent>(
        this.modalService.open(ConfirmStopVotingComponent).instance
      )).result
        .asObservable()
        .first()
        .toPromise();
      if (!confirm) return;
      await this.voting.stopVoting();
    }
    if (this.itemIndex > 0) {
      this.itemIndex--;
      this.noMoreItemsToEstimate =
        this.items.filter(
          (x, i) => i > this.itemIndex && x.featureType !== "Bug"
        ).length === 0;
    }
  }

  toastCopyToClipboard() {
    this.toastService.show(
      "Link wurde in die Zwischenablage kopiert!",
      4000,
      "confirm-toast"
    );
  }

  /**
   * set estimate-password value and if its required
   */
  setEstimatePassword() {
    this.projectService
      .setEstimatePassword(this.project)
      .then(() => {
        this.toastService.show("Passwort gespeichert!", 4000, "confirm-toast");
      })
      .catch((res) => {
        this.toastService.show(
          "Fehler! Nicht Gespeichert",
          4000,
          "notification-toast"
        );
      });
  }

  async onVotingChanges() {
    await this.featureService.saveFeature(this.items[this.itemIndex]);
  }

  async onKeyDown(event) {
    if (event.key === "ArrowRight") {
      await this.updateSlidesNext();
    }
    if (event.key === "ArrowLeft") {
      await this.updateSlidesPrev();
    }
  }

  async showAttachments() {
    this.items[this.itemIndex].numberOfAttachments = await this.gallery.show(
      this.items[this.itemIndex]._id,
      this.items[this.itemIndex].agirId,
      "Item"
    );
    this.changeDetectorRef.detectChanges();
  }
}



// WEBPACK FOOTER //
// /agir/src/app/components/item-carousel/item-carousel.component.ts